import * as React from 'react';
import * as TogglePrimitive from '@radix-ui/react-toggle';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

/**
 * An on/off button (DESIGN_SYSTEM.md §5 "Pressed and expanded"). Radix sets
 * aria-pressed and data-state; the accessible name stays constant.
 *
 * Pressed uses the kit's "selected" tint (nav item, DialogSectionRail):
 * bg-primary/10 text-primary. Hover stays bg-accent — and a pressed toggle
 * under the pointer deepens to bg-primary/15 — so "on" never looks like hover.
 * Never flip a Button's variant to show state: bg-secondary and bg-accent are
 * the same colour.
 */
const toggleVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:hover:bg-primary/15 data-[state=on]:hover:text-primary',
  {
    variants: {
      variant: {
        ghost: '',
        outline: 'border border-input bg-background data-[state=on]:border-primary/30',
      },
      size: {
        default: 'h-9 px-3',
        sm: 'h-8 px-2',
        icon: 'h-9 w-9',
        'icon-xs': 'h-7 w-7',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'default',
    },
  },
);

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> & VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root ref={ref} className={cn(toggleVariants({ variant, size, className }))} {...props} />
));
Toggle.displayName = TogglePrimitive.Root.displayName;

export { Toggle, toggleVariants };
